import { ApiClient } from '@/services/http/apiClient';
import type { ApiResponse, Order, OrderAdminResponse, OrderInput, Pagination } from '@/types/api';

export const OrderService = {
  // Público
  async create(body: OrderInput): Promise<Order> {
    const response = await ApiClient.post<ApiResponse<{ order: Order }>>('/orders', { body });
    if (!response.success) {
      throw new Error(response.message || 'Erro ao criar pedido');
    }
    return response.data.order;
  },
  async get(id: number): Promise<Order> {
    const response = await ApiClient.get<ApiResponse<{ order: Order }>>(`/orders/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Erro ao buscar pedido');
    }
    return response.data.order;
  },

  // Autenticado (usuário)
  async listMy(): Promise<Order[]> {
    const response = await ApiClient.get<ApiResponse<{ orders: Pagination<Order> }>>('/orders');
    if (!response.success) {
      throw new Error(response.message || 'Erro ao listar pedidos');
    }
    return response.data.orders.data;
  },

  // Admin
  admin: {
    async list(params?: { page?: number; per_page?: number; status?: string }) {
      const response = await ApiClient.get<ApiResponse<{ orders: Pagination<OrderAdminResponse> }>>(`/admin/orders`, { query: params });
      if (!response.success) {
        throw new Error(response.message || 'Erro ao listar pedidos');
      }
      return response.data.orders;
    },
    async update(id: number, data: Partial<Order>) {
      const response = await ApiClient.put<ApiResponse<Order>>(`/admin/orders/${id}`, { body: data });
      if (!response.success) {
        throw new Error(response.message || 'Erro ao atualizar pedido');
      }
      return response.data;
    },
    async delete(id: number) {
      const response = await ApiClient.delete<ApiResponse<null>>(`/admin/orders/${id}`);
      if (!response.success) {
        throw new Error(response.message || 'Erro ao remover pedido');
      }
      return response;
    },
    async confirm(id: number) {
      const response = await ApiClient.patch<ApiResponse<Order>>(`/admin/orders/${id}/confirm`);
      if (!response.success) {
        throw new Error(response.message || 'Erro ao confirmar pedido');
      }
      return response.data;
    },
    async cancel(id: number) {
      const response = await ApiClient.patch<ApiResponse<Order>>(`/admin/orders/${id}/cancel`);
      if (!response.success) {
        throw new Error(response.message || 'Erro ao cancelar pedido');
      }
      return response.data;
    },
    async updateStatus(id: number, status: string) {
      const response = await ApiClient.patch<ApiResponse<Order>>(`/admin/orders/${id}/status`, { body: { status } });
      if (!response.success) {
        throw new Error(response.message || 'Erro ao atualizar status do pedido');
      }
      return response.data;
    },
    async stats(): Promise<{ total: number; confirmed: number; canceled: number; pending: number }> {
      const response = await ApiClient.get<ApiResponse<{ total: number; confirmed: number; canceled: number; pending: number }>>(`/admin/orders/stats`);
      if (!response.success) {
        throw new Error(response.message || 'Erro ao buscar estatísticas de pedidos');
      }
      return response.data;
    },
    async statistics(): Promise<{ total: number; confirmed: number; canceled: number; pending: number }> {
      const response = await ApiClient.get<ApiResponse<{ total: number; confirmed: number; canceled: number; pending: number }>>(`/admin/orders/statistics`);
      if (!response.success) {
        throw new Error(response.message || 'Erro ao buscar estatísticas de pedidos');
      }
      return response.data;
    },
    async export(): Promise<string> {
      const response = await ApiClient.get<ApiResponse<string>>(`/admin/orders/export`);
      if (!response.success) {
        throw new Error(response.message || 'Erro ao exportar pedidos');
      }
      return response.data;
    },
    async whatsappUrl(id: number): Promise<{ whatsapp_url: string; whatsapp_number?: string | null }> {
      const response = await ApiClient.get<ApiResponse<{ whatsapp_url: string; whatsapp_number?: string | null }>>(`/admin/orders/${id}/whatsapp`);
      if (!response.success) {
        throw new Error(response.message || 'Erro ao obter URL do WhatsApp');
      }
      return response.data;
    },
    async generateWhatsApp(id: number): Promise<{ whatsapp_url: string; whatsapp_number?: string | null }> {
      const response = await ApiClient.post<ApiResponse<{ whatsapp_url: string; whatsapp_number?: string | null }>>(`/admin/orders/${id}/whatsapp`);
      if (!response.success) {
        throw new Error(response.message || 'Erro ao gerar URL do WhatsApp');
      }
      return response.data;
    },
  },
};